/**
 * @fileoverview Content resolution utility for v2 schema
 * @module screen-view-v2/utils/contentResolver
 *
 * Resolves localized content (text, images) from v2 schema objects.
 * Handles locale fallback and template variable substitution.
 *
 * Key v1 → v2 changes:
 * - l10n → translations
 * - Asset.assetUrl.origin/thumb/small/medium/normal → AssetReference { original?, small? }
 * - resolveText → resolveLabel
 * - resolveAssetUrl → resolveAssetReference
 * - resolveImageUrl → resolveLocalizedImageUrl
 */

import type { LocalizedLabel, LocalizedImage, LocalizedClip, AssetReference } from '@segmently/schema-models/v2';
import { substituteVariables, type TemplateVariables } from './templateParser';

// Re-export TemplateVariables for convenience
export type { TemplateVariables };

/**
 * Available asset sizes in v2 AssetReference.
 * V2 only has 2 sizes: 'original' and 'small'.
 */
export type AssetSize = 'original' | 'small';

/**
 * Gets the first available locale from a translations object.
 *
 * @param translations - The translations object
 * @returns The first available locale key, or null if empty
 */
function getFirstAvailableLocale<T>(translations: Record<string, T>): string | null {
  const keys = Object.keys(translations);
  return keys.length > 0 ? keys[0] : null;
}

/**
 * Gets a value from translations object with locale fallback.
 *
 * Fallback chain:
 * 1. Exact match: locale 'es-ES' → translations['es-ES']
 * 2. Language-only match: locale 'es' → translations['es-ES'] (or 'es-ES' → 'es')
 * 3. First available locale
 *
 * @param translations - The translations object
 * @param locale - The requested locale (BCP 47 or short code)
 * @returns The value for the locale or first available, null if none
 */
function getLocaleValue<T>(translations: Record<string, T>, locale: string): T | null {
  // 1. Exact match
  if (locale in translations) {
    return translations[locale];
  }

  // 2. Language-only match: 'es' finds 'es-ES', or 'es-ES' finds 'es'
  const shortCode = locale.split('-')[0].toLowerCase();
  const fuzzyKey = Object.keys(translations).find(
    k => k.split('-')[0].toLowerCase() === shortCode
  );
  if (fuzzyKey) {
    return translations[fuzzyKey];
  }

  // 3. Fall back to first available locale
  const fallbackLocale = getFirstAvailableLocale(translations);
  if (fallbackLocale) {
    return translations[fallbackLocale];
  }

  return null;
}

/**
 * Resolves text content from a LocalizedLabel's translations.
 *
 * Looks up text for the specified locale, falls back to first available locale
 * if not found. Optionally substitutes template variables.
 *
 * @param label - LocalizedLabel from v2 schema (optional)
 * @param locale - The locale to resolve (e.g., "en-US")
 * @param variables - Optional variables for template substitution
 * @returns The resolved text, or null if not found
 *
 * @example
 * resolveLabel({ translations: { 'en-US': 'Hello' }, ... }, 'en-US')
 * // Returns: 'Hello'
 *
 * @example
 * resolveLabel({ translations: { 'en-US': 'Price: @price' }, ... }, 'en-US', { price: '$9.99' })
 * // Returns: 'Price: $9.99'
 */
export function resolveLabel(
  label: LocalizedLabel | undefined,
  locale: string,
  variables?: TemplateVariables
): string | null {
  if (!label || !label.translations) {
    return null;
  }

  const text = getLocaleValue(label.translations, locale);

  if (text === null || text === undefined) {
    return null;
  }

  // Apply template variable substitution if variables provided
  if (variables && Object.keys(variables).length > 0) {
    return substituteVariables(text, variables);
  }

  return text;
}

/**
 * Resolves asset reference from a LocalizedImage's translations with configurable size.
 *
 * Looks up asset for the specified locale, falls back to first available locale
 * if not found. Returns the URL for the specified size.
 *
 * Fallback order: requested size → small → original.
 *
 * @param image - LocalizedImage from v2 schema (optional)
 * @param locale - The locale to resolve (e.g., "en-US")
 * @param size - The asset size to use (default: 'small')
 * @returns The resolved asset URL, or null if not found
 *
 * @example
 * resolveAssetReference({ translations: { 'en-US': { small: 'https://...' } }, ... }, 'en-US')
 *
 * @example
 * resolveAssetReference({ translations: { 'en-US': { original: 'https://...' } }, ... }, 'en-US', 'original')
 */
export function resolveAssetReference(
  image: LocalizedImage | undefined,
  locale: string,
  size: AssetSize = 'small'
): string | null {
  if (!image || !image.translations) {
    return null;
  }

  const assetRef: AssetReference | null = getLocaleValue(image.translations, locale);

  if (!assetRef) {
    return null;
  }

  // Try requested size first, then fall back through sizes
  // Fallback order: requested → small → original
  const fallbackOrder: AssetSize[] = [size, 'small', 'original'];
  for (const fallbackSize of fallbackOrder) {
    const url = assetRef[fallbackSize];
    if (url) {
      return url;
    }
  }

  return null;
}

/**
 * Resolves asset reference from a LocalizedClip's translations with configurable size.
 *
 * Same logic as resolveAssetReference but accepts LocalizedClip.
 * LocalizedClip has identical translations structure: { [key: string]: AssetReference }.
 *
 * @param clip - LocalizedClip from v2 schema (optional)
 * @param locale - The locale to resolve (e.g., "en-US")
 * @param size - The asset size to use (default: 'small')
 * @returns The resolved clip URL, or null if not found
 */
export function resolveClipReference(
  clip: LocalizedClip | undefined,
  locale: string,
  size: AssetSize = 'small'
): string | null {
  if (!clip || !clip.translations) {
    return null;
  }

  const assetRef: AssetReference | null = getLocaleValue(clip.translations, locale);

  if (!assetRef) {
    return null;
  }

  // Same fallback order as resolveAssetReference
  const fallbackOrder: AssetSize[] = [size, 'small', 'original'];
  for (const fallbackSize of fallbackOrder) {
    const url = assetRef[fallbackSize];
    if (url) {
      return url;
    }
  }

  return null;
}

/**
 * Resolves image URL from a LocalizedImage's translations.
 *
 * Convenience wrapper around resolveAssetReference using 'small' size.
 *
 * @param image - LocalizedImage from v2 schema (optional)
 * @param locale - The locale to resolve (e.g., "en-US")
 * @returns The resolved image URL (small size), or null if not found
 */
export function resolveLocalizedImageUrl(
  image: LocalizedImage | undefined,
  locale: string
): string | null {
  return resolveAssetReference(image, locale, 'small');
}
